import React, { useState, useEffect, useRef } from 'react';
import { Eye, ArrowRight, Clock, TrendingUp } from 'lucide-react';
import { useVocabulary } from '../context/VocabularyContext';
import { srsManager } from '../utils/srsManager';

export function FlashcardMode() {
  const { state, dispatch, actions } = useVocabulary();
  const { sessionConfig, sessionProgress } = state;
  const { sentences, words, poolCode, type } = sessionConfig;
  const { currentWordIndex } = sessionProgress;
  
  const [showAnswer, setShowAnswer] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [reactionTime, setReactionTime] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [showSRSButtons, setShowSRSButtons] = useState(false);
  const [reactionEvaluation, setReactionEvaluation] = useState(null);
  const inputRef = useRef(null);
  
  // Determine if this is a word or sentence session
  const isWordSession = type === 'vocabulary';
  const items = isWordSession ? words : sentences;
  const category = isWordSession ? 'words' : 'sentences';
  
  // SRS Integration
  const [smartSession, setSmartSession] = useState(null);
  const [isSmartSessionEnabled] = useState(true);
  
  // Initialize available items ONCE (all items ready for review)
  useEffect(() => {
    if (isSmartSessionEnabled && items && items.length > 0 && !smartSession) {
      // Check if items are already SRS objects (have 'item' field)
      const areAlreadyFiltered = items[0] && typeof items[0] === 'object' && 'item' in items[0];
      
      // Check if items are from random session (have '_poolCode' field)
      const areRandomSession = items[0] && typeof items[0] === 'object' && '_poolCode' in items[0];
      
      let availableItems;
      if (areAlreadyFiltered) {
        // Items are already filtered SRS objects from single pool session
        availableItems = items;
      } else if (areRandomSession) {
        // Items are from random session - already filtered, just wrap them
        availableItems = items.map(item => ({ 
          id: item.id, 
          type: item._type || 'review', 
          item: item, 
          srsData: item._srsData || null 
        }));
      } else if (poolCode) {
        // Items are raw, need to filter them
        availableItems = srsManager.getAvailableSentencesForPool(category, poolCode, items);
      } else {
        // No poolCode, treat all items as available (fallback)
        availableItems = items.map((item, index) => ({ id: index, type: 'review', item, srsData: null }));
      }
      
      // Shuffle for randomization
      const shuffled = [...availableItems].sort(() => Math.random() - 0.5);
      setSmartSession(shuffled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, poolCode, isSmartSessionEnabled, category]); // Intentionally excluding smartSession to load ONCE
  
  // Use available items if smart session is enabled, otherwise all items
  const currentItem = smartSession ? smartSession[currentWordIndex]?.item : items[currentWordIndex];
  const totalItems = smartSession ? smartSession.length : items.length;
  const currentSessionItem = smartSession ? smartSession[currentWordIndex] : null;
  const progress = ((currentWordIndex + 1) / totalItems) * 100;
  
  // Get SRS stats for current pool
  const srsStats = poolCode ? srsManager.getStats(category, poolCode) : null;
  
  // Extract text from item (adapt to different JSON structures)
  const getPolishText = (item) => {
    if (!item) return '';
    return item.polish || item.polish_word || '';
  };
  
  const getGermanText = (item) => {
    if (!item) return '';
    return item.german || item.german_word || '';
  };
  
  const currentSentence = currentItem; // Keep name for compatibility

  // Move to next card
  const nextWord = () => {
    if (currentWordIndex + 1 >= totalItems) {
      // Session finished - go to results
      dispatch({ type: actions.SET_APP_STATE, payload: 'results' });
    } else {
      // Move to next item
      dispatch({ 
        type: actions.SET_CURRENT_WORD_INDEX, 
        payload: currentWordIndex + 1 
      });
    }
  };

  // Reset state when moving to next card
  useEffect(() => {
    setShowAnswer(false);
    setUserInput('');
    setReactionTime(null);
    setStartTime(Date.now());
    setShowSRSButtons(false);
    setReactionEvaluation(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentWordIndex]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleShowAnswer = () => {
    if (showAnswer) return;
    
    const endTime = Date.now();
    const reaction = startTime ? endTime - startTime : null;
    
    setReactionTime(reaction);
    setShowAnswer(true);
    
    // Oceń czas reakcji i przygotuj przyciski SRS
    if (reaction && userInput.trim() === '') { // Tylko jeśli nic nie wpisano
      const evaluation = srsManager.evaluateReactionTime(reaction);
      setReactionEvaluation(evaluation);
      setShowSRSButtons(evaluation.buttons.length > 0);
    } else {
      setShowSRSButtons(false);
    }
  };

  const handleNext = () => {
    // Jeśli nie było oceny SRS, automatycznie jako słabe
    if (reactionTime && !userInput.trim() && showAnswer && !showSRSButtons) {
      handleSRSAction('auto_poor');
    } else {
      // Record the interaction (for backward compatibility)
      const answerRecord = {
        word: currentSentence,
        categoryKey: currentSentence.tense || 'vocabulary',
        categoryName: currentSentence.tense ? `Czas ${currentSentence.tense}` : 'Słownictwo',
        userAnswer: userInput,
        correctAnswer: getGermanText(currentSentence),
        reactionTime: reactionTime,
        hasUserInput: userInput.trim() !== ''
      };

      dispatch({ 
        type: actions.ADD_ANSWER, 
        payload: answerRecord 
      });
      
      nextWord();
    }
  };

  const handleSRSAction = (action) => {
    // For random sessions, use the _poolCode from the current session item
    const effectivePoolCode = poolCode || currentSessionItem?.item?._poolCode;
    
    if (effectivePoolCode && reactionTime) {
      // For random sessions, use the item's id which is the original index in its pool
      // For regular sessions, use the smart session id or current index
      let effectiveItemIndex;
      
      if (!poolCode && currentSessionItem?.item?._poolCode) {
        // Random session - use the item's original id from its pool
        effectiveItemIndex = currentSessionItem.id;
      } else {
        // Regular session - use smart session id or current index
        effectiveItemIndex = currentSessionItem ? currentSessionItem.id : currentWordIndex;
      }
      
      console.log(`SRS Update: category=${category}, pool=${effectivePoolCode}, index=${effectiveItemIndex}, action=${action}`);
      
      // Aktualizuj element w systemie SRS
      srsManager.updateItem(
        category,
        effectivePoolCode,
        effectiveItemIndex,
        reactionTime,
        action
      );
      
      // Record for backward compatibility
      const answerRecord = {
        word: currentSentence,
        categoryKey: currentSentence.tense || 'vocabulary',
        categoryName: currentSentence.tense ? `Czas ${currentSentence.tense}` : 'Słownictwo',
        userAnswer: userInput,
        correctAnswer: getGermanText(currentSentence),
        reactionTime: reactionTime,
        srsAction: action
      };

      dispatch({ 
        type: actions.ADD_ANSWER, 
        payload: answerRecord 
      });
    }
    
    // Przejdź do następnego słowa bez odświeżania sesji
    nextWord();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (!showAnswer) {
        handleShowAnswer();
      } else {
        handleNext();
      }
    }
  };

  if (!currentSentence) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Fiszki - Zdania {smartSession && '(SRS)'}
              </h2>
              {smartSession && currentSessionItem && (
                <div className="text-xs text-gray-500 mt-1">
                  {currentSessionItem.type === 'review' ? (
                    <span className="inline-flex items-center">
                      <Clock size={12} className="mr-1" />
                      Powtórka - Poziom {currentSessionItem.srsData.level}
                    </span>
                  ) : (
                    <span className="text-green-600">Nowe zdanie</span>
                  )}
                </div>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {currentWordIndex + 1} / {totalItems}
              {smartSession && items && totalItems !== items.length && (
                <div className="text-xs">({items.length} łącznie w puli)</div>
              )}
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* SRS Stats */}
          {srsStats && (
            <div className="flex justify-between text-xs text-gray-600 border-t pt-3">
              <div>
                <span className="font-medium">{srsStats.total_items}</span> w puli
              </div>
              <div>
                <span className="font-medium">{srsStats.due_for_review}</span> dostępnych
              </div>
              {srsStats.avg_reaction_time > 0 && (
                <div>
                  Śr. czas: <span className="font-medium">{(srsStats.avg_reaction_time / 1000).toFixed(1)}s</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Flashcard */}
        <div className="bg-white rounded-xl shadow-xl p-8 mb-6 min-h-[400px] flex flex-col justify-center">
          {!showAnswer ? (
            /* Question state */
            <div className="text-center">
              <h3 className="text-2xl text-gray-700 mb-8">
                {getPolishText(currentSentence)}
              </h3>
              
              {/* Optional input field */}
              <div className="mb-8">
                <input
                  ref={inputRef}
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Opcjonalnie wpisz tłumaczenie..."
                  className="w-full p-4 text-lg border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-center"
                />
              </div>

              {/* Show button */}
              <button
                onClick={handleShowAnswer}
                className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center mx-auto"
              >
                <Eye className="mr-2" size={20} />
                Pokaż odpowiedź
              </button>
              
              <div className="mt-4 text-sm text-gray-500">
                lub naciśnij Enter
              </div>
            </div>
          ) : (
            /* Answer state */
            <div className="text-center">
              <h3 className="text-xl text-gray-600 mb-4">
                {getPolishText(currentSentence)}
              </h3>
              
              <h1 className="text-3xl font-bold text-gray-800 mb-6">
                {getGermanText(currentSentence)}
              </h1>
              
              {/* Show user input if provided */}
              {userInput.trim() && (
                <h1 className="text-3xl text-blue-600 mb-6 border-2 border-blue-200 rounded-lg p-4">
                  {userInput}
                </h1>
              )}
              
              {/* Show reaction time */}
              {!userInput.trim() && reactionTime && (
                <div className="mb-6">
                  <div className="text-lg text-gray-500 mb-2 flex items-center justify-center">
                    <Clock className="mr-2" size={16} />
                    {(reactionTime / 1000).toFixed(1)}s
                  </div>
                </div>
              )}

              {/* SRS Action Buttons */}
              {showSRSButtons && reactionEvaluation && (
                <div className="mb-6">
                  <div className="text-sm text-gray-600 mb-3">
                    <TrendingUp className="inline mr-1" size={14} />
                    Jak dobrze znałeś to zdanie?
                  </div>
                  <div className="grid gap-3 max-w-lg mx-auto">
                    {reactionEvaluation.buttons.map((button, index) => {
                      const getSizeClasses = (size) => {
                        switch(size) {
                          case 'large':
                            return 'px-8 py-4 text-lg font-semibold';
                          case 'medium':
                            return 'px-6 py-3 text-base font-medium';
                          case 'small':
                            return 'px-4 py-2 text-sm font-medium';
                          default:
                            return 'px-6 py-3 text-base font-medium';
                        }
                      };
                      
                      return (
                        <button
                          key={index}
                          onClick={() => handleSRSAction(button.action)}
                          className={`rounded-lg transition-colors ${getSizeClasses(button.size)} ${
                            button.color === 'red' ? 'bg-red-100 hover:bg-red-200 text-red-700' :
                            button.color === 'green' ? 'bg-green-100 hover:bg-green-200 text-green-700' :
                            button.color === 'blue' ? 'bg-blue-100 hover:bg-blue-200 text-blue-700' :
                            button.color === 'purple' ? 'bg-purple-100 hover:bg-purple-200 text-purple-700' :
                            button.color === 'orange' ? 'bg-orange-100 hover:bg-orange-200 text-orange-700' :
                            'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          {button.text}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Next button (when no SRS buttons or after manual input) */}
              {(!showSRSButtons || userInput.trim()) && (
                <>
                  <button
                    onClick={handleNext}
                    className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center mx-auto"
                  >
                    <ArrowRight className="mr-2" size={20} />
                    Następne
                  </button>
                  
                  <div className="mt-4 text-sm text-gray-500">
                    lub naciśnij Enter
                  </div>
                </>
              )}
            </div>
          )}
        </div>


      </div>
    </div>
  );
}
